/* component-companion-gift.js — "Gratis Matratzenschoner zum Premium Topper" companion gift (ES module, every page).

   RULE: every Premium Komfort Topper unit in the cart earns ONE Matratzenschoner of the SAME size (width x length)
   for free. 2 toppers 140x200 -> 2 protectors 140x200. No per-order cap. The automatic Function discount makes the
   gift lines 0 €; this module only keeps the CART CONTENT in parity with that rule.

   CONFIG (layout renders <script type="application/json" data-kk-companion>):
     { buy: [product ids], buyVariants: { "<topper variant id>": "140x200" },
       gift: { product: <id>, variants: { "140x200": { id: <variant id>, available: true } } },
       start: "YYYY-MM-DD HH:MM" (optional), end: "YYYY-MM-DD HH:MM", tz: "Europe/Berlin", perUnit: 1, cartUrl: "/cart" }
     Missing/invalid config, a `start` still in the future or an `end` already in the past (parsed in `tz`) -> nothing runs.

   CART CONTRACT (window.KK.cart from global.js): add(item, {sections}) / change(lineKey, qty, {sections}) -> Promise<cart>;
   every mutation publishes 'kk:cart:updated' { cart, added?, changed?: {key, quantity, previous}, updated?: {updates} }.
   Gift lines carry the line property _gift = "true". A protector bought normally (no flag) also counts as the free one
   when a matching topper is present (parity with earlier promos), so we never add a second one on top of it.

   planReconcile(cart, cfg, declined) is PURE (exported, unit-tested in tests/companion-gift.test.mjs):
     -> [{ op:'add', id, quantity, properties:{_gift:'true'}, size }, { op:'change', key, quantity, size }]
     need[size]  = topper units * perUnit;  normal[size] = un-flagged protector qty;  want = max(0, need - normal)
     cur < want  -> one 'add' of the difference (skipped for declined / unavailable / unknown sizes)
     cur > want  -> first gift line := want, every further gift line of that size := 0 (declined sizes still trimmed)

   RUNTIME: initial cart = KK.cart.state / KK.cart.get(); then every kk:cart:updated snapshot:
     · a gift line the CUSTOMER changed to fewer units / removed -> its size is DECLINED (sessionStorage
       kk_companion_declined); our own trims are recognised by their line keys and never count as a decline
     · a buy product was added -> its size is un-declined (the customer wants the topper again)
     · then plan + apply ops SEQUENTIALLY (adds first, then changes) with { sections:[cart-drawer section id] } so the
       drawer re-renders. The PAGE-LOAD pass is SILENT (plain /cart/add.js + KK.cart.reconcile()) so a returning
       visitor's drawer does not pop open uninvited (only an `added` payload opens the drawer).
     · echoes of our own mutations are skipped, max 3 passes per trigger, a size whose add was rejected is not retried on
       this page view, 3 consecutive failures stop the module for this page view. No KK.cart -> plain endpoints + reload.
     · the cart PAGE re-renders itself from kk:cart:updated (sections/main-cart.liquid) - no reload needed there.

   PDP CARD: [data-companion-card data-map='{ topper:{"<variant id>":"140x200"}, sizes:{"140x200":{id,size_fmt,uvp_fmt,
   price_fmt,available}} }'] > [data-companion-line data-tpl="Matratzenschoner {size} · UVP {uvp} → 0,00 €" data-soldout="…"].
   Re-rendered for the current topper variant on load and on kk:variant:changed (payload.variant.id, optionally filtered by
   the card's data-section-id); DOM is built with createElement/textContent only: {size} -> <span class="companion-line__name">,
   {uvp} -> <s>, {price} (or the text after the last "→"/"->") -> <b>. Unknown/unavailable size -> data-soldout + .is-soldout.
   Touches window ONLY inside init() (guarded), so node can import planReconcile. No external imports. */

const isGiftLine = (it) => !!(it && it.properties && it.properties._gift);
const num = (v) => Number(v);

// "YYYY-MM-DD HH:MM" in an IANA tz -> epoch ms (same logic as kk-countdown.js; DST-safe via Intl, two passes).
function tzOffsetMs(date, tz) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const p = {};
  f.formatToParts(date).forEach((x) => { p[x.type] = x.value; });
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - date.getTime();
}
export function parseInTz(str, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(str || '').trim());
  if (!m) return NaN;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0);
  let off = tzOffsetMs(new Date(guess), tz);
  off = tzOffsetMs(new Date(guess - off), tz);
  return guess - off;
}

// size key ("140x200") of a gift-product variant id, from cfg.gift.variants
export function giftSizeOf(cfg, variantId) {
  const vs = (cfg && cfg.gift && cfg.gift.variants) || {};
  return Object.keys(vs).find((size) => vs[size] && String(vs[size].id) === String(variantId)) || null;
}

// Gift size that serves a topper size: the exact size when available, else cfg.fallbacks[size] when that one is available
// (Jonas 2026-09-11: 80 x 200 stock is low -> {"80x200":"90x200"}). Returns { size, fallback:boolean } or null.
export function resolveGiftSize(cfg, size) {
  const vs = (cfg && cfg.gift && cfg.gift.variants) || {};
  if (vs[size] && vs[size].available && vs[size].id != null) return { size, fallback: false };
  const alt = (cfg.fallbacks || {})[size];
  if (alt && vs[alt] && vs[alt].available && vs[alt].id != null) return { size: alt, fallback: true, original: size };
  return null;
}

export function planReconcile(cart, cfg, declined) {
  const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
  const buy = new Set((cfg.buy || []).map(num));
  const giftProduct = num(cfg.gift && cfg.gift.product);
  const perUnit = Math.max(0, num(cfg.perUnit == null ? 1 : cfg.perUnit) || 0);
  const decl = declined instanceof Set ? declined : new Set(declined || []);
  const need = {}, normal = {}, giftLines = {}, viaFallback = {};
  items.forEach((it) => {
    const pid = num(it.product_id);
    if (buy.has(pid)) {
      const size = (cfg.buyVariants || {})[String(it.id)];
      if (!size) return;
      const res = resolveGiftSize(cfg, size);
      const key = res ? res.size : size;          // unresolvable sizes keep their key: nothing is added, extras are still trimmed
      need[key] = (need[key] || 0) + (num(it.quantity) || 0) * perUnit;
      if (res && res.fallback) viaFallback[key] = res.original;
    } else if (pid === giftProduct) {
      const size = giftSizeOf(cfg, it.id);
      if (!size) return;
      if (isGiftLine(it)) (giftLines[size] || (giftLines[size] = [])).push(it);
      else normal[size] = (normal[size] || 0) + (num(it.quantity) || 0);
    }
  });
  const adds = [], changes = [];
  new Set([...Object.keys(need), ...Object.keys(giftLines)]).forEach((size) => {
    const want = Math.max(0, (need[size] || 0) - (normal[size] || 0));
    const lines = giftLines[size] || [];
    const cur = lines.reduce((n, it) => n + (num(it.quantity) || 0), 0);
    const v = cfg.gift.variants[size];
    if (cur < want) {
      if (!decl.has(size) && v && v.available && v.id != null) {
        const props = { _gift: 'true' };
        // substitute size: a visible line property tells the customer (and the packer) why the size differs
        if (viaFallback[size] && cfg.note) {
          const fmt = (k) => (cfg.gift.variants[k] && cfg.gift.variants[k].title) || k.replace('x', ' x ') + ' cm';
          props[cfg.noteKey || 'Hinweis'] = String(cfg.note).replace(/\{size\}/g, fmt(viaFallback[size])).replace(/\{alt\}/g, fmt(size));
        }
        adds.push({ op: 'add', id: num(v.id), quantity: want - cur, properties: props, size });
      }
    } else if (cur > want) {
      lines.forEach((it, i) => {
        const q = i === 0 ? want : 0;
        if (num(it.quantity) !== q) changes.push({ op: 'change', key: it.key, quantity: q, size });
      });
    }
  });
  return adds.concat(changes);
}

/* ─────────────────────────── browser runtime ─────────────────────────── */
function init() {
  if (window.__kkCompanionBound) return;
  window.__kkCompanionBound = true;
  let cfg = null;
  try { cfg = JSON.parse((document.querySelector('script[data-kk-companion]') || {}).textContent || ''); } catch (e) { cfg = null; }
  const KK = window.KK || {};
  // PDP cards render whenever the block exists (their own countdown hides them at `end`); only the CART runtime is gated
  const onReady = (fn) => { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true }); else fn(); };
  onReady(() => initCards(KK));
  if (!cfg || !Array.isArray(cfg.buy) || !cfg.gift || !cfg.gift.variants || !cfg.buyVariants) return;
  const tz = cfg.tz || 'Europe/Berlin';
  const end = parseInTz(cfg.end, tz);
  if (isNaN(end) || end <= Date.now()) return;
  const startAt = parseInTz(cfg.start, tz);
  if (!isNaN(startAt) && startAt > Date.now()) return;   // published early: cards may show, the cart stays untouched

  const native = !!(KK.cart && typeof KK.cart.add === 'function' && typeof KK.cart.change === 'function');
  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
    .then((r) => { if (!r.ok) throw new Error('cart request failed'); return r.json().catch(() => null); });
  const plain = { add: (item) => post('/cart/add.js', { items: [item] }), change: (key, q) => post('/cart/change.js', { id: key, quantity: q }) };
  const loud = native ? { add: (item, o) => KK.cart.add(item, o), change: (key, q, o) => KK.cart.change(key, q, o) } : plain;
  const STORE = 'kk_companion_declined';
  const declined = new Set();
  try { (JSON.parse(window.sessionStorage.getItem(STORE) || '[]') || []).forEach((s) => declined.add(String(s))); } catch (e) { /* private mode */ }
  const persist = () => { try { window.sessionStorage.setItem(STORE, JSON.stringify([...declined])); } catch (e) { /* noop */ } };
  const failed = new Set();   // sizes whose add the server rejected on this page view
  const ownKeys = new Set();  // line keys we changed ourselves -> never read as a customer decline
  let busy = false, primed = false, lastCart = null, failures = 0;

  const sectionsOpt = () => {
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    return drawer ? { sections: [drawer.getAttribute('data-section-id')] } : {};
  };
  // silent = page-load pass: plain endpoints + KK.cart.reconcile() -> the drawer repaints but does not open (no `added`)
  const applyOps = (ops, silent) => {
    const api = silent ? plain : loud;
    const opts = sectionsOpt();
    let mutated = false;
    let p = Promise.resolve(null);
    ops.forEach((o) => {
      if (o.op === 'change') ownKeys.add(o.key);
      p = p.then(() => (o.op === 'add'
        ? api.add({ id: o.id, quantity: o.quantity, properties: o.properties }, opts).catch((e) => { failed.add(o.size); throw e; })
        : api.change(o.key, o.quantity, opts)
      ).then((cart) => { mutated = true; return cart; }));
    });
    return p.then((cart) => ({ cart: cart && Array.isArray(cart.items) ? cart : null, mutated }), (e) => { if (mutated) return { cart: null, mutated }; throw e; });
  };
  const afterSilent = () => {
    if (native && typeof KK.cart.reconcile === 'function') { try { KK.cart.reconcile(); } catch (e) { /* noop */ } return; }
    window.location.reload();
  };
  const run = (cart, silent) => {
    if (busy || !cart || failures >= 3) return;
    busy = true;
    let passes = 0, mutated = false;
    const step = (c) => {
      const ops = planReconcile(c, cfg, new Set([...declined, ...failed]));
      if (!ops.length || passes >= 3) return Promise.resolve(c);
      passes += 1;
      return applyOps(ops, silent).then((r) => {
        mutated = mutated || r.mutated; failures = 0;
        if (r.cart) return step(r.cart);
        // plain endpoints do not return the full cart -> read it once and continue
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((x) => x.json()).then(step).catch(() => c);
      });
    };
    step(cart).then((c) => { lastCart = c; }, () => { failures += 1; }).then(() => {
      busy = false;
      if (!mutated) return;
      if (silent) afterSilent();
      else if (!native) window.location.reload();
    });
  };
  const declineLine = (line) => { const s = line && isGiftLine(line) && giftSizeOf(cfg, line.id); if (s) { declined.add(s); persist(); } };
  const onCartUpdated = (p) => {
    const cart = (p && p.cart) || (KK.cart && KK.cart.state);
    const prev = lastCart || (KK.cart && KK.cart.state) || cart;
    // decline signals are read even while we are busy - only our own line keys are ignored
    if (p && p.changed) {
      if (ownKeys.has(p.changed.key)) ownKeys.delete(p.changed.key);
      else if (p.changed.previous && isGiftLine(p.changed.previous) && num(p.changed.quantity) < num(p.changed.previous.quantity)) declineLine(p.changed.previous);
    }
    if (p && p.updated && p.updated.updates && prev) {
      Object.keys(p.updated.updates).forEach((key) => {
        if (ownKeys.has(key)) { ownKeys.delete(key); return; }
        const line = (prev.items || []).find((it) => it.key === key || String(it.id) === String(key));
        if (line && isGiftLine(line) && num(p.updated.updates[key]) < num(line.quantity)) declineLine(line);
      });
    }
    if (busy) return;   // our own echo (the reconcile loop re-plans on the cart our mutation returned)
    primed = true;
    if (!cart || !Array.isArray(cart.items)) return;
    if (p && p.added && Array.isArray(p.added.items)) {
      p.added.items.forEach((it) => {
        const s = it && (cfg.buy.map(num).indexOf(num(it.product_id)) !== -1) && cfg.buyVariants[String(it.id)];
        if (s && declined.delete(s)) persist();
      });
    }
    lastCart = cart;
    run(cart, false);
  };

  const start = () => {
    if (KK.pubsub) KK.pubsub.subscribe('kk:cart:updated', onCartUpdated);
    if (KK.cart && KK.cart.state) { lastCart = KK.cart.state; run(KK.cart.state, true); return; }
    const read = (native && typeof KK.cart.get === 'function')
      ? KK.cart.get()
      : fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    read.then((cart) => { if (!primed) { lastCart = cart; run(cart, true); } }).catch(() => {});
  };
  onReady(start);
}

function initCards(KK) {
  document.querySelectorAll('[data-companion-card]').forEach((card) => {
    let map = null;
    try { map = JSON.parse(card.getAttribute('data-map') || ''); } catch (e) { map = null; }
    const line = card.querySelector('[data-companion-line]');
    if (!map || !map.sizes || !line) return;
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; n.textContent = text; return n; };
    const note = card.querySelector('[data-companion-note]');
    const render = (variantId) => {
      const size = (map.topper || {})[String(variantId)];
      let s = size && map.sizes[size];
      let noteText = '';
      // sold out in the exact size -> configured substitute size + note (same rule as the cart module)
      if (size && !(s && s.available)) {
        const alt = (map.fallbacks || {})[size];
        const a = alt && map.sizes[alt];
        if (a && a.available) {
          const exact = (map.sizes[size] && map.sizes[size].size_fmt) || size.replace('x', ' x ') + ' cm';
          noteText = String(map.note || '').replace(/\{size\}/g, exact).replace(/\{alt\}/g, a.size_fmt || alt);
          s = a;
        }
      }
      if (note) { note.textContent = noteText; note.hidden = !noteText; }
      line.textContent = '';
      line.classList.toggle('is-soldout', !(s && s.available));
      if (!(s && s.available)) { line.textContent = line.getAttribute('data-soldout') || ''; return; }
      const tpl = line.getAttribute('data-tpl') || 'Matratzenschoner {size} · UVP {uvp} → {price}';
      const parts = tpl.split(/(\{size\}|\{uvp\}|\{price\})/);
      let buf = '';
      const flush = () => { if (buf) line.appendChild(document.createTextNode(buf)); buf = ''; };
      parts.forEach((t, i) => {
        if (t === '{size}') { line.appendChild(el('span', 'companion-line__name', buf + (s.size_fmt || size))); buf = ''; }
        else if (t === '{uvp}') { flush(); line.appendChild(el('s', '', s.uvp_fmt || '')); }
        else if (t === '{price}') { flush(); line.appendChild(el('b', '', s.price_fmt || '')); }
        else if (i === parts.length - 1 && tpl.indexOf('{price}') === -1 && /(→|->)/.test(t)) {
          const k = Math.max(t.lastIndexOf('→'), t.lastIndexOf('->')); const cut = k + (t[k] === '-' ? 2 : 1);
          buf += t.slice(0, cut) + ' '; flush(); line.appendChild(el('b', '', t.slice(cut).trim()));
        } else buf += t;
      });
      flush();
    };
    const current = () => {
      const form = card.closest('form');
      const inp = (form && form.querySelector('[name=id]')) || document.querySelector('[data-variant-id-input]');
      return inp ? inp.value : '';
    };
    render(current());
    const sid = card.getAttribute('data-section-id');
    if (KK.pubsub) KK.pubsub.subscribe('kk:variant:changed', (p) => {
      if (!p || !p.variant || p.variant.id == null || (sid && p.sectionId && p.sectionId !== sid)) return;
      render(p.variant.id);
    });
  });
}

if (typeof window !== 'undefined') init();
